import { Box, CircularProgress, Typography } from '@mui/material';
import { PlusIcon } from 'lucide-react';
import { BuilderBox } from '../../types/SimilarlySituated/BuilderBox.type';
import BuildCard from './BuildCard';
import BuildsToolbar from './BuildsToolbar';

const cardSx = {
  backgroundColor: '#fff',
  border: '1px solid #d6d6d6',
  borderRadius: '8px',
  padding: '24px',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  transitionProperty: 'border-color, box-shadow',
  transition: '0.3s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': { borderColor: 'secondary.main', boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' },
};

interface BuildsViewProps {
  builds: BuilderBox[];
  isLoading: boolean;
  activeSort: string;
  searchText: string;
  viewOption: 'list' | 'grid';
  onSortChange: (sort: string) => void;
  onSearchChange: (searchText: string) => void;
  onToggleView: () => void;
  onOpenBuild: (build: BuilderBox) => void;
  onAddBuild: () => void;
  onDuplicateBuild: (build: BuilderBox) => void;
  onBuildRenamed: (build: BuilderBox) => void;
  onBuildDeleted: (buildId: number) => void;
}

function BuildsView({
  builds,
  isLoading,
  activeSort,
  searchText,
  viewOption,
  onSortChange,
  onSearchChange,
  onToggleView,
  onOpenBuild,
  onAddBuild,
  onDuplicateBuild,
  onBuildRenamed,
  onBuildDeleted,
}: BuildsViewProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', paddingBottom: '32px' }}>
      <Typography sx={{ fontSize: '22px', fontWeight: 600 }}>Saved Boxes</Typography>

      <BuildsToolbar
        activeSort={activeSort}
        viewOption={viewOption}
        searchText={searchText}
        onSort={onSortChange}
        onToggleView={onToggleView}
        onSearchChange={onSearchChange}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: '80px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: viewOption === 'grid' ? 'repeat(auto-fill, minmax(290px, 1fr))' : '1fr',
            gap: '16px',
          }}
        >
          {builds.map((build) => (
            <BuildCard
              key={build.id}
              build={build}
              activeSort={activeSort}
              onOpen={() => onOpenBuild(build)}
              onDuplicate={() => onDuplicateBuild(build)}
              onRenamed={onBuildRenamed}
              onDeleted={onBuildDeleted}
            />
          ))}

          {builds.length === 0 && (
            <Typography sx={{ fontSize: '14px', color: '#718096', gridColumn: '1 / -1' }}>
              No boxes match your search.
            </Typography>
          )}

          <Box
            component='button'
            type='button'
            onClick={onAddBuild}
            sx={{
              ...cardSx,
              padding: '12px',
              borderStyle: 'dashed',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              color: '#d6d6d6',
              minHeight: viewOption === 'grid' ? '200px' : '140px',
              '&:hover': { borderColor: 'secondary.main', backgroundColor: '#eff5fa' },
            }}
          >
            <PlusIcon size={20} color='#4A5568' />
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#4A5568' }}>Add Box</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default BuildsView;
